import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AppShell } from "../../components/Sidebar";
import { StatusBadge } from "../../components/StatusBadge";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { gradeSubjectOptions, subjectLabel } from "../../lib/subjects";
import { Trophy, Award, BarChart3, Play } from "lucide-react";

export default function QuizLanding() {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [subject, setSubject] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    api.get(`/student/quizzes?lang=${i18n.resolvedLanguage || "en"}`).then((r) => setData(r.data));
  }, [i18n.resolvedLanguage]);

  if (!data) return <AppShell role="student"><p className="text-slate-500">{t("common.loading")}</p></AppShell>;
  const s = data.summary;
  const subjectOptions = gradeSubjectOptions(user?.grade);
  const rows = data.rows.filter((r) => subject === "all" || r.subject === subject);

  return (
    <AppShell role="student">
      <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">{t("student.quiz.title")}</h1>
      <p className="mt-1 text-slate-500">{t("student.quiz.subtitle")}</p>

      <div className="mt-7 grid gap-4 lg:grid-cols-4">
        <SummaryCard icon={BarChart3} color="bg-indigo-100 text-indigo-600" label={t("student.quiz.quizzes_attempted")} value={s.quizzes_attempted} />
        <SummaryCard icon={Award} color="bg-amber-100 text-amber-600" label={t("student.quiz.avg_quiz_score")} value={`${s.average_score}%`} />
        <SummaryCard icon={Trophy} color="bg-emerald-100 text-emerald-600" label={t("student.quiz.best_quiz_score")} value={`${s.best_score}%`} />
        <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{t("student.quiz.overall_performance")}</p>
          <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <PerfDot label={t("types.Conceptual")} value={s.overall_conceptual} />
            <PerfDot label={t("types.Applied")} value={s.overall_applied} />
            <PerfDot label={t("types.Theoretical")} value={s.overall_theoretical} />
          </div>
        </div>
      </div>

      <div className="mt-7 rounded-2xl border border-slate-100 bg-white p-6 shadow-card">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-bold text-slate-900">{t("student.quiz.all_quizzes")}</h3>
            <p className="text-sm text-slate-500">{t("student.quiz.all_quizzes_sub")}</p>
          </div>
          <select
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            data-testid="student-quiz-filter-subject"
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            <option value="all">{t("student.dashboard.all_subjects", { defaultValue: "All Subjects" })}</option>
            {subjectOptions.map((so) => <option key={so} value={so}>{subjectLabel(so)}</option>)}
          </select>
        </div>
        <div className="mt-5 overflow-x-auto">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead>
              <tr className="border-b border-slate-100 text-xs font-semibold uppercase tracking-wide text-slate-500">
                <th rowSpan={2} className="py-3 pr-4 align-bottom">#</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.quiz.table.module_name")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.quiz.table.subject")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.quiz.table.quiz_score")}</th>
                <th colSpan={3} className="pb-1 text-center font-semibold text-slate-700">{t("student.quiz.table.quiz_performance")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.quiz.table.last_attempted")}</th>
                <th rowSpan={2} className="py-3 pr-4 align-bottom">{t("student.quiz.table.action")}</th>
              </tr>
              <tr className="border-b border-slate-100 text-xs font-semibold text-slate-500">
                <th className="py-2 pr-4">{t("types.Conceptual")}</th>
                <th className="py-2 pr-4">{t("types.Applied")}</th>
                <th className="py-2 pr-4">{t("types.Theoretical")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.module_id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="py-4 pr-4 text-slate-500">{i + 1}</td>
                  <td className="py-4 pr-4 font-medium text-slate-900">{r.module_name}</td>
                  <td className="py-4 pr-4 text-slate-700">{t(`subjects.${r.subject}`, { defaultValue: r.subject })}</td>
                  <td className="py-4 pr-4 font-semibold text-slate-700">{r.quiz_score != null ? `${r.quiz_score}%` : "—"}</td>
                  <td className="py-4 pr-4"><StatusBadge value={r.conceptual} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={r.applied} withDot compact /></td>
                  <td className="py-4 pr-4"><StatusBadge value={r.theoretical} withDot compact /></td>
                  <td className="py-4 pr-4 text-slate-600">{r.last_attempted || "—"}</td>
                  <td className="py-4 pr-4">
                    <button
                      onClick={() => navigate(`/student/quiz/${r.module_id}`)}
                      data-testid={`start-quiz-${r.module_id}`}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow hover:bg-indigo-700"
                    >
                      <Play size={12} /> {t("student.quiz.start_quiz")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AppShell>
  );
}

const SummaryCard = ({ icon: Icon, color, label, value }) => (
  <div className="rounded-2xl border border-slate-100 bg-white p-5 shadow-card">
    <div className="flex items-start gap-3">
      <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${color}`}><Icon size={20} /></div>
      <div>
        <p className="text-xs font-medium text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
      </div>
    </div>
  </div>
);

const PerfDot = ({ label, value }) => {
  const { t } = useTranslation();
  const dotColor = value === "Solid" ? "bg-emerald-500" : value === "Moderate" ? "bg-amber-500" : value === "Weak" ? "bg-rose-500" : "bg-slate-300";
  return (
    <div className="rounded-xl bg-slate-50 p-2.5 text-center">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{label}</p>
      <div className="mt-1.5 flex items-center justify-center gap-1.5">
        <span className={`h-2 w-2 rounded-full ${dotColor}`} />
        <span className="text-xs font-bold text-slate-700">{t(`status.${value}`, { defaultValue: value })}</span>
      </div>
    </div>
  );
};
